import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * Personalizzazione della demo dalla landing (?mezzo=furgoni&quanti=6)
 * + tracciamento eventi del funnel sullo stesso canale gtag/dataLayer
 * gia' usato dal sito (nessuna libreria aggiuntiva).
 */
@Injectable({
  providedIn: 'root'
})
export class DemoFlowService {

  static readonly LABELS: any = {
    'furgoni': { singolare: 'Furgone', plurale: 'furgoni', icona: 'assets/img/icons-fdc/f-cars.svg' },
    'auto': { singolare: 'Auto', plurale: 'auto', icona: 'assets/img/icons-fdc/f-cars.svg' },
    'mezzi pesanti': { singolare: 'Mezzo pesante', plurale: 'mezzi pesanti', icona: 'assets/img/icons-fdc/f-cars.svg' },
    'rimorchi': { singolare: 'Rimorchio', plurale: 'rimorchi e attrezzature', icona: 'assets/img/icons-fdc/f-cars.svg' }
  };

  /**
   * Il punto d'incontro fra la chat e il giro guidato: dalla chat si può
   * chiedere "Tour guidato", e chi lo sa fare è il componente del tour, che
   * vive altrove nel guscio dell'app. Un canale qui evita che i due
   * componenti debbano conoscersi.
   */
  readonly tourRichiesto$ = new Subject<void>();

  /**
   * Sul telefono la demo si apre con la guida di benvenuto, e solo dopo
   * arrivano i cartellini che spiegano le singole pagine: due cose insieme
   * sarebbero una sopra l'altra. Questi due lo tengono in ordine.
   */
  guidaDiBenvenutoAperta = false;
  readonly guidaDiBenvenutoChiusa$ = new Subject<void>();

  constructor() {
    this.captureParamsFromUrl();
  }

  /** Chiamato dalla chat: chiede al giro guidato di ripartire. */
  chiediTour(): void {
    this.tourRichiesto$.next();
  }

  /**
   * Porta alla pagina di richiesta della prova gratuita.
   *
   * È una pagina statica dentro gli asset dell'app, quindi sulla stessa
   * origine: login e id_browser si portano dietro da soli. Si punta al file
   * vero e non all'indirizzo /prova perché quello esiste solo grazie a un
   * rewrite di Firebase, che in locale non c'è.
   */
  vaiAllaRichiestaProva(): void {
    this.track('cta_cliccata', { destinazione: 'richiesta_prova' });
    window.location.href = '/assets/prova/index.html';
  }

  /** Legge ?mezzo= e ?quanti= dall'URL e li persiste per la sessione. */
  captureParamsFromUrl(): void {
    try {
      const url = new URL(window.location.href);
      const mezzo = url.searchParams.get('mezzo');
      const quanti = url.searchParams.get('quanti');
      if (mezzo) sessionStorage.setItem('demo_mezzo', mezzo.toLowerCase().replace(/-/g, ' '));
      if (quanti) sessionStorage.setItem('demo_quanti', quanti);
    } catch (err) { /* niente parametri, demo generica */ }
  }

  /** Tipo di mezzo scelto in landing ('furgoni' | 'auto' | 'mezzi pesanti' | 'rimorchi') o null. */
  get mezzo(): string | null {
    const m = sessionStorage.getItem('demo_mezzo');
    return m && DemoFlowService.LABELS[m] ? m : null;
  }

  /** Etichetta plurale per i testi ("6 furgoni"), con fallback generico. */
  get mezzoPlurale(): string {
    return this.mezzo ? DemoFlowService.LABELS[this.mezzo].plurale : 'mezzi';
  }

  get mezzoSingolare(): string {
    return this.mezzo ? DemoFlowService.LABELS[this.mezzo].singolare : 'Veicolo demo';
  }

  /** Numero di veicoli demo da mostrare: 1..6, default 3. */
  get quanti(): number {
    const raw = parseInt(sessionStorage.getItem('demo_quanti') || '', 10);
    if (!raw || isNaN(raw)) return 3;
    return Math.min(6, Math.max(1, raw));
  }

  /** Query string da propagare a /free-trial. */
  get queryString(): string {
    if (!this.mezzo && !sessionStorage.getItem('demo_quanti')) return '';
    const p: string[] = [];
    if (this.mezzo) p.push('mezzo=' + encodeURIComponent(this.mezzo));
    p.push('quanti=' + this.quanti);
    return '?' + p.join('&');
  }

  /** Testo CTA personalizzato: "Provalo sui tuoi 6 furgoni — localizzatore gratis". */
  get ctaLabel(): string {
    return 'Provalo sui tuoi ' + this.quanti + ' ' + this.mezzoPlurale + ' \u2014 localizzatore gratis';
  }

  /**
   * Eventi funnel: demo_aperta, demo_passo_1..4_completato, cta_mostrata,
   * cta_cliccata, free_trial_aperto, free_trial_inviato, uscita_intercettata.
   */
  track(evento: string, extra?: any): void {
    try {
      const w: any = window as any;
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push(Object.assign({ event: evento, mezzo: this.mezzo || 'non_indicato', quanti: this.quanti }, extra || {}));
      if (typeof w.gtag === 'function') w.gtag('event', evento, Object.assign({ mezzo: this.mezzo || 'non_indicato', quanti: this.quanti }, extra || {}));
    } catch (err) { console.error('demo track', err); }
  }
}
